// === Account Settings Component ===
// frontend/src/components/AccountSettings.js
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Home, Utensils, User, Settings } from 'lucide-react';

const AccountSettings = () => {
  const { user, logout, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '', newPassword: '', confirmPassword: ''
  });

  const [deleteForm, setDeleteForm] = useState({
    password: '', confirmDelete: ''
  });

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
  };

  const handleDeleteChange = (e) => {
    const { name, value } = e.target;
    setDeleteForm(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/user/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Password changed successfully');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setError(data.error || 'Password change failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();
    
    if (deleteForm.confirmDelete !== 'DELETE') {
      setError('Please type "DELETE" to confirm account deletion');
      return;
    }

    if (isAdmin()) {
      setError('Admin accounts cannot be deleted');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE}/user/delete-account`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: deleteForm.password }),
      });

      const data = await response.json();

      if (response.ok) {
        alert('Account deleted successfully');
        logout();
      } else {
        setError(data.error || 'Account deletion failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Back button and breadcrumb */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => window.location.href = '/dashboard'}
                className="flex items-center text-gray-600 hover:text-green-600 transition-colors"
              >
                <ArrowLeft size={20} className="mr-2" />
                <span className="font-medium">Back to Dashboard</span>
              </button>
              <div className="hidden sm:block text-gray-300">|</div>
              <div className="hidden sm:flex items-center space-x-2 text-sm text-gray-500">
                <Home size={16} />
                <span>/</span>
                <Settings size={16} />
                <span>Account Settings</span>
              </div>
            </div>

            {/* Center - Navigation Links */}
            <nav className="hidden md:flex space-x-6">
              <a
                href="/dashboard"
                className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors px-3 py-2 rounded-md text-sm font-medium"
              >
                <Home size={18} />
                <span>Dashboard</span>
              </a>
              <button
                onClick={() => {
                  window.location.href = '/dashboard';
                  // Note: You might want to use React Router's navigate instead
                  // and pass state to set the meals view
                }}
                className="flex items-center space-x-2 text-gray-600 hover:text-green-600 transition-colors px-3 py-2 rounded-md text-sm font-medium"
              >
                <Utensils size={18} />
                <span>All Meals</span>
              </button>
            </nav>

            {/* Right side - User info */}
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-700">{user?.username}</p>
                <p className="text-xs text-green-600">
                  {user?.role === 'admin' ? 'Administrator' : 'User'}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <User size={20} className="text-green-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-6 px-4">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
            <p className="mt-1 text-sm text-gray-600">Manage your account preferences and security settings</p>
          </div>

          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {['profile', 'password', 'danger'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab === 'profile' && 'Profile'}
                  {tab === 'password' && 'Change Password'}
                  {tab === 'danger' && 'Danger Zone'}
                </button>
              ))}
              {isAdmin() && (
                <button
                  onClick={() => setActiveTab('admin')}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'admin'
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Admin Panel
                </button>
              )}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'profile' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Profile Information</h3>
                
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Username</label>
                    <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md">
                      {user?.username}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md">
                      {user?.email}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user?.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {user?.role}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">2FA Status</label>
                    <div className="mt-1 p-3 bg-gray-50 border border-gray-300 rounded-md">
                      {user?.role === 'admin' ? 'Required' : 'Not Required'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'password' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
                
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                      Current Password
                    </label>
                    <input
                      type="password" id="currentPassword" name="currentPassword" required
                      value={passwordForm.currentPassword} onChange={handlePasswordChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                      New Password
                    </label>
                    <input
                      type="password" id="newPassword" name="newPassword" required
                      value={passwordForm.newPassword} onChange={handlePasswordChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                      Confirm New Password
                    </label>
                    <input
                      type="password" id="confirmPassword" name="confirmPassword" required
                      value={passwordForm.confirmPassword} onChange={handlePasswordChange}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  
                  {error && <p className="text-sm text-red-600">{error}</p>}
                  {message && <p className="text-sm text-green-600">{message}</p>}
                  
                  <button
                    type="submit" disabled={loading}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  >
                    {loading ? 'Changing Password...' : 'Change Password'}
                  </button>
                </form>
              </div>
            )}

            {activeTab === 'danger' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-red-900">Danger Zone</h3>
                
                {!isAdmin() ? (
                  <div className="border border-red-300 rounded-lg p-6">
                    <h4 className="text-red-900 font-medium mb-4">Delete Account</h4>
                    <p className="text-sm text-red-700 mb-4">
                      Once you delete your account, there is no going back. This action cannot be undone.
                    </p>
                    
                    <form onSubmit={handleDeleteAccount} className="space-y-4 max-w-md">
                      <div>
                        <label htmlFor="deletePassword" className="block text-sm font-medium text-red-700">
                          Enter your password to confirm
                        </label>
                        <input
                          type="password" id="deletePassword" name="password" required
                          value={deleteForm.password} onChange={handleDeleteChange}
                          className="mt-1 block w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="confirmDelete" className="block text-sm font-medium text-red-700">
                          Type "DELETE" to confirm
                        </label>
                        <input
                          type="text" id="confirmDelete" name="confirmDelete" required
                          value={deleteForm.confirmDelete} onChange={handleDeleteChange}
                          placeholder="DELETE"
                          className="mt-1 block w-full px-3 py-2 border border-red-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                      
                      {error && <p className="text-sm text-red-600">{error}</p>}
                      
                      <button
                        type="submit" disabled={loading}
                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        {loading ? 'Deleting Account...' : 'Delete Account'}
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="border border-gray-300 rounded-lg p-6">
                    <p className="text-sm text-gray-600">
                      Admin accounts cannot be deleted for security reasons.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'admin' && isAdmin() && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Admin Panel</h3>
                
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div className="bg-green-50 p-6 rounded-lg">
                    <h4 className="text-green-900 font-medium mb-2">User Management</h4>
                    <p className="text-sm text-green-700 mb-4">View and manage all user accounts</p>
                    <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm">
                      Manage Users
                    </button>
                  </div>
                  
                  <div className="bg-blue-50 p-6 rounded-lg">
                    <h4 className="text-blue-900 font-medium mb-2">System Settings</h4>
                    <p className="text-sm text-blue-700 mb-4">Configure application settings</p>
                    <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm">
                      System Config
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Helper - Only visible on small screens */}
      <div className="md:hidden fixed bottom-4 right-4">
        <button
          onClick={() => window.location.href = '/dashboard'}
          className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-full shadow-lg transition-colors"
          title="Back to Dashboard"
        >
          <Home size={20} />
        </button>
      </div>
    </div>
  );
};

export default AccountSettings;
